import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { PlusCircle, PiIcon } from "lucide-react";
import AdunitFilters from "../Components/Adunits/AdunitFilters";
import AdunitStats from "../Components/Adunits/AdunitStats";
import AdunitTable from "../Components/Adunits/AdunitTable";
import AdunitFormModal from "../Components/Adunits/AdunitFormModal";
import adunitService from "../Services/adunitService";

export default function Adunits() {
  const [adunits, setAdunits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    id: null,
    name: "",
    type: "Banner",
    status: "active"
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");

  // Fetch adunits on component mount
  useEffect(() => {
    const fetchAdunits = async () => {
      try {
        setLoading(true);
        const response = await adunitService.getAdUnits();
        setAdunits(response.data || response); // Handle different API response structures
      } catch (err) {
        toast.error("Failed to load adunits");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAdunits();
  }, []);

  // Filter adunits based on search term and selected type
  const filteredAdunits = adunits.filter(unit => {
    const nameMatch = unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
    const typeMatch = selectedType === "all" || unit.type === selectedType;
    return nameMatch && typeMatch;
  });

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setForm({ id: null, name: "", type: "Banner", status: "active" });
    setIsEditing(false);
    setModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    debugger
    try {
      if (isEditing) {
        await adunitService.updateAdUnit(form.id, form);
        toast.success("Adunit updated successfully");
      } else {
        await adunitService.createAdUnit(form);
        toast.success("Adunit created successfully");
      }

      // Refresh the list
      const response = await adunitService.getAdUnits();
      setAdunits(response.data || response);
    } catch (err) {
      toast.error("Failed to save adunit");
      console.error(err);
    }

    closeModal();
  };

  const handleEdit = (unit) => {
    setForm(unit);
    setIsEditing(true);
    openModal();
  };

  const handleDelete = async (unit) => {
    if (!window.confirm("Are you sure you want to delete this adunit?")) return;

    try {
      await adunitService.deleteAdUnit(unit.id);
      toast.success("Adunit deleted successfully");

      // Refresh the list
      const response = await adunitService.getAdUnits();
      setAdunits(response.data || response);
    } catch (err) {
      toast.error("Failed to delete adunit");
      console.error(err);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedType("all");
  };

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <header className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xl md:text-2xl lg:text-3xl font-extrabold flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"
        >
          <PiIcon className="w-6 h-6 text-pink-500 animate-bounce-slow" />
          AdUnit Management
        </motion.h2>
        <button
          onClick={openModal}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg"
        >
          <PlusCircle className="w-5 h-5" />
          Create New AdUnit
        </button>
      </header>

      <AdunitFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        onReset={resetFilters}
      />

      <AdunitStats
        total={adunits.length}
        active={adunits.filter(u => u.status === "active").length}
      />

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <AdunitTable
          adunits={filteredAdunits}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <AdunitFormModal
        open={modalOpen}
        close={closeModal}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        isEditing={isEditing}
      />
    </div>
  );
}